import { useEffect, useRef, useState } from "react";
import { mountPlanetField, PlanetFieldScene } from "@/lib/planet/planet-field";

/**
 * Decorative planet-field background for the auth layout.
 *
 * Mount once inside the auth page's outer container (position:relative,
 * overflow:hidden). The scene stays alive across login ↔ register
 * transitions because it lives on the page, not inside the form.
 *
 * - Canvas: aria-hidden, pointer-events:none, never blocks input.
 * - Pause button: accessible (aria-pressed), top-right, does not overlap
 *   the form (form is max-w-sm, centered; button sits in the corner).
 * - reduced motion / hidden tab / unmount: handled by the scene module.
 * - No-Canvas fallback: the .auth-planet class paints a static radial
 *   gradient (see styles.css) — the form renders identically.
 */
export function PlanetBackground() {
  const rootRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sceneRef = useRef<PlanetFieldScene | null>(null);
  const [paused, setPaused] = useState(false);
  const [supported, setSupported] = useState(
    () => typeof document !== "undefined" && !!document.createElement("canvas").getContext,
  );

  useEffect(() => {
    const root = rootRef.current;
    const canvas = canvasRef.current;
    if (!root || !canvas) return;
    // Detect a real 2d context up front: if the browser cannot provide one,
    // skip mounting entirely — the static .auth-planet gradient is the
    // fallback and the pause button is not rendered.
    if (!canvas.getContext("2d")) {
      setSupported(false);
      return;
    }
    const scene = mountPlanetField(root, canvas, { onPauseChange: setPaused });
    sceneRef.current = scene;
    return () => {
      scene.dispose();
      sceneRef.current = null;
    };
  }, []);

  return (
    <div
      ref={rootRef}
      className="auth-planet"
      style={{
        position: "absolute",
        inset: 0,
        overflow: "hidden",
        pointerEvents: "none",
        isolation: "isolate",
      }}
    >
      <canvas
        ref={canvasRef}
        aria-hidden="true"
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }}
      />
      {supported && (
        <button
          type="button"
          aria-pressed={paused}
          aria-label={paused ? "Включить анимацию" : "Пауза анимации"}
          title={paused ? "Включить анимацию" : "Пауза анимации"}
          onClick={() => sceneRef.current?.setPaused(!paused)}
          style={{
            position: "absolute",
            top: 16,
            right: 16,
            pointerEvents: "auto",
            zIndex: 2,
          }}
          className="rounded-lg border border-white/10 bg-white/5 px-2.5 py-1.5 text-xs text-white/60 backdrop-blur-sm transition-colors hover:bg-white/10 hover:text-white/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/60"
        >
          {paused ? "Включить анимацию" : "Пауза анимации"}
        </button>
      )}
    </div>
  );
}
